import { useCallback, useEffect, useState } from 'react';
import { api, ItemAnalysisPayload, ItemStat, Subject } from '../api';
import { Icon } from '../Icon';

/**
 * How each question performs in practice.
 *
 * Ordered hardest-first, because the reason to open this screen is almost
 * always "which questions are going wrong?". A low score alone is not a fault —
 * some items are meant to be hard — so the row also carries the two signals
 * that separate a hard question from a broken one:
 *
 *  - WHICH wrong option people picked. When nearly everyone converges on the
 *    same wrong answer, that answer is usually right and the key is wrong.
 *  - Discrimination: how much better, overall, the people who got this item
 *    right scored than those who got it wrong. Negative means the strongest
 *    candidates are the ones getting it wrong, which is the classic signature
 *    of a miskeyed item.
 *
 * Editor-only. The choice breakdown gives the answer away.
 */

/**
 * The list shows everything by default. Items below the reporting threshold
 * are not hidden — an editor needs to see that a question has barely been
 * attempted — they simply do not get a percentage attached to them.
 */
const DEFAULT_MIN = 1;

export function ItemAnalysis({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<ItemStat[]>([]);
  const [threshold, setThreshold] = useState(10);
  const [unanimousMin, setUnanimousMin] = useState(4);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [packId, setPackId] = useState('');
  const [minAnswers, setMinAnswers] = useState(DEFAULT_MIN);
  const [sort, setSort] = useState<'percent_correct' | 'discrimination'>('percent_correct');
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const q = new URLSearchParams({ minAnswers: String(minAnswers), sort });
      if (packId) q.set('packId', packId);
      const d = await api.get<ItemAnalysisPayload>(`/admin/item-analysis?${q}`);
      setRows(d.items);
      setThreshold(d.reportingThreshold);
      setUnanimousMin(d.unanimousMin);
    } catch (e: any) { setErr(e.message); }
  }, [packId, minAnswers, sort]);

  useEffect(() => { api.get<Subject[]>('/subjects').then(setSubjects).catch(() => {}); }, []);
  useEffect(() => { void load(); }, [load]);

  const field = {
    font: 'inherit', padding: '0.5rem', minHeight: '2.75rem',
    border: '1px solid var(--border-strong)', borderRadius: '8px',
    background: 'var(--surface)', color: 'var(--ink)',
  } as const;

  /**
   * A row worth an editor's attention, and why.
   *
   * Proportional concerns wait for the reporting threshold — flagging "almost
   * nobody gets this right" off three answers is precisely the false alarm the
   * threshold exists to prevent. Unanimity is the one exception, and it is
   * labelled provisional: everyone independently choosing the SAME other
   * answer is a different kind of evidence from a low rate, and waiting for
   * ten leaves a likely miskey in front of learners for longer.
   */
  function concern(r: ItemStat): { text: string; provisional: boolean } | null {
    const keyed = r.correctResponse?.[0];

    if (r.unanimousWrong) {
      const chose = Object.keys(r.choiceCounts)[0]?.toUpperCase();
      return {
        text: `All ${r.answers} answers chose ${chose}, not the keyed ${keyed?.toUpperCase()}`,
        provisional: !r.reportable,
      };
    }

    if (!r.reportable) return null;

    const top = Object.entries(r.choiceCounts).sort((a, b) => b[1] - a[1])[0];
    if (top && keyed && top[0] !== keyed && top[1] / r.answers >= 0.6) {
      return {
        text: `Most people chose ${top[0].toUpperCase()}, not the keyed ${keyed.toUpperCase()}`,
        provisional: false,
      };
    }
    if (r.discrimination < 0) {
      return { text: 'Stronger candidates are getting this wrong', provisional: false };
    }
    if (r.percentCorrect <= 25) {
      return { text: 'Almost nobody gets this right', provisional: false };
    }
    return null;
  }

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Question performance</h1>
      <p className="muted">
        How often each question is answered correctly, and which option people pick instead.
        Hardest first. A low score is not automatically a fault — but when almost everyone
        converges on one wrong option, the key is usually what is wrong.
      </p>
      <p className="muted">
        A percentage is only shown once an item has at least <strong>{threshold} answers</strong>.
        Below that the counts are still listed, because "nobody has tried this yet" is worth
        knowing — but a rate from two or three answers can only read 0%, 50% or 100%, and each of
        those looks like a finding while being nothing of the sort.
      </p>

      {err && <div className="notice notice--warn" role="alert">{err}</div>}

      <div className="btn-row" style={{ marginBottom: '1rem' }}>
        <span>
          <label htmlFor="ia-pack" className="visually-hidden">Subject</label>
          <select id="ia-pack" value={packId} onChange={(e) => setPackId(e.target.value)} style={field}>
            <option value="">All subjects</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.subject}</option>)}
          </select>
        </span>
        <span>
          <label htmlFor="ia-min" className="visually-hidden">Minimum answers</label>
          <select id="ia-min" value={minAnswers} style={field}
                  onChange={(e) => setMinAnswers(Number(e.target.value))}>
            {[1, 3, 5, 10, 25].map((n) => (
              <option key={n} value={n}>at least {n} answer{n === 1 ? '' : 's'}</option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor="ia-sort" className="visually-hidden">Sort by</label>
          <select id="ia-sort" value={sort} style={field}
                  onChange={(e) => setSort(e.target.value as any)}>
            <option value="percent_correct">Lowest score first</option>
            <option value="discrimination">Worst discrimination first</option>
          </select>
        </span>
      </div>

      <p className="muted">
        {rows.length} question{rows.length === 1 ? '' : 's'} answered at least {minAnswers} time
        {minAnswers === 1 ? '' : 's'} · {rows.filter((r) => r.reportable).length} with enough
        answers to report a rate
      </p>

      {rows.length === 0 ? (
        <p className="muted">
          Nothing yet at this threshold. Lower the minimum, or wait for more answers.
        </p>
      ) : rows.map((r) => {
        const why = concern(r);
        const keyed = r.correctResponse?.[0];
        return (
          <article key={`${r.packId}-${r.externalId}`} className="card">
            <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>
              {r.externalId}{' '}
              {r.reportable ? (
                <span className={`pill ${r.percentCorrect >= 50 ? 'pill--ok' : 'pill--bad'}`}>
                  {r.percentCorrect}% correct
                </span>
              ) : (
                <span className="pill">
                  {r.answers} of {threshold} answers — no rate yet
                </span>
              )}
              {r.openDisputes > 0 && (
                <span className="pill pill--bad" style={{ marginLeft: '0.4rem' }}>
                  ⚑ {r.openDisputes} open report{r.openDisputes === 1 ? '' : 's'}
                </span>
              )}
            </h2>

            <p style={{ marginBottom: '0.5rem' }}>{r.stem}</p>

            <p className="muted" style={{ marginBottom: '0.5rem' }}>
              {r.subject} · {r.answers} answered · {r.correct} right · {r.wrong} wrong
              {/* Rates and discrimination are both proportions of a small
                  sample, so they are withheld together rather than one of them
                  lending false authority to the other. */}
              {r.reportable
                ? ` · ${r.percentWrong}% wrong · discrimination ${r.discrimination}`
                : ' · too few answers to report a rate'}
              {r.versions > 1 && ` · pooled across ${r.versions} versions`}
            </p>

            {/* Pooling across versions is only sound while the key stayed put. */}
            {r.keyChanged && (
              <div className="notice notice--warn" style={{ marginBottom: '0.5rem' }}>
                The correct answer differs between versions of this item, so these figures
                combine answers to two different questions. Treat them as unreliable.
              </div>
            )}

            <ul className="review-options">
              {Object.entries(r.choiceCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([choice, n]) => {
                  const isKey = choice === keyed;
                  const share = Math.round((n / r.answers) * 100);
                  return (
                    <li key={choice}
                        className={`review-option${isKey ? ' review-option--correct' : ''}`}>
                      <span className="choice-key">{choice.toUpperCase()}.</span>
                      {n} of {r.answers} ({share}%)
                      {isKey && <strong className="choice-tag choice-tag--ok"> ✓ the keyed answer</strong>}
                    </li>
                  );
                })}
            </ul>

            {why && (
              <div className="notice notice--warn" style={{ marginBottom: 0 }}>
                <strong>{why.provisional ? 'Early signal:' : 'Worth a look:'}</strong> {why.text}.
                {why.provisional && (
                  <> Based on fewer than {threshold} answers, so treat it as a lead rather than a
                  conclusion — but {unanimousMin} or more people independently choosing the same
                  other answer is usually worth checking now rather than later.</>
                )}
              </div>
            )}
          </article>
        );
      })}
    </>
  );
}
